import { useMemo, useState } from 'react';
import { Card, CardHeader, StatTile } from '../../../components/ui/Card';
import { Modal } from '../../../components/ui/Modal';
import { EmptyState } from '../../../components/ui/States';
import { InfoTip } from '../../../components/ui/InfoTip';
import { SegmentedControl } from '../../../components/ui/Tabs';
import type { DealKind, SalesOverview, SalesPeriodFilter, TopDeal } from '../../../api/projectManagement';
import { SALES_REPS } from '../../ProjectManagement/team';
import { formatStage, plural } from '../../ProjectManagement/formatters';
import { formatMoneyCompact } from '../../../utils/numberFormat';
import { AttioLink, RepInitials, dealCompanyLine, formatShortDate, humanizeDealName, majorityOwner } from '../shared';
import { Badge } from '../../../components/ui/Badge';
import { formatDayMonthYear } from '../../../utils/dateFormat';
import { PeriodFilter, describePeriod } from '../../ProjectManagement/Sales/PeriodFilter';
import { DEAL_KIND_LABEL, currentQuarter, isPreAttioWin } from '../../ProjectManagement/Sales/dealKinds';
import { isPlaceholderCloseDate, splitDefaultClose } from './quarters';

export function formatCloseDate(iso: string | null): string {
  if (!iso) return 'No close date';
  return formatDayMonthYear(iso);
}

export { PLACEHOLDER_CLOSE_DATE, isPlaceholderCloseDate, splitDefaultClose } from './quarters';

/** Today as YYYY-MM-DD on the viewer's calendar, to compare with close dates. */
function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * The company under a deal name. Dropped when the deal name already says it
 * ("HENSOLDT · Tender · 7" under HENSOLDT); a missing link is always said.
 */
export function CompanySubLine({ name, dealName }: { name: string | null; dealName: string | null }) {
  if (!name) return <p className="text-body-xs text-text-tertiary/70 truncate">No company linked</p>;
  const line = dealCompanyLine(dealName, name);
  return line ? <p className="text-body-xs text-text-tertiary truncate">{line}</p> : null;
}

/** How many overdue deals the strip names before "and N more". */
const OVERDUE_NAMED = 4;

/**
 * Exceptions to act on in Attio, above everything else: open deals whose close
 * date has passed, deals still on the default close date, and stale deals.
 * Each line only appears when it has something in it.
 */
function PipelineExceptions({ data, onOpenStale }: { data: SalesOverview; onOpenStale: () => void }) {
  const today = todayYmd();
  const overdue = data.openDeals
    .filter((d) => d.expectedCloseDate && d.expectedCloseDate.slice(0, 10) < today)
    .sort((a, b) => (a.expectedCloseDate ?? '').localeCompare(b.expectedCloseDate ?? ''));
  const placeholder = data.openDeals.filter((d) => isPlaceholderCloseDate(d.expectedCloseDate));
  const { staleCount, staleValueEur } = data.totals;
  if (overdue.length === 0 && placeholder.length === 0 && staleCount === 0) return null;

  const named = overdue.slice(0, OVERDUE_NAMED);
  const sum = (deals: TopDeal[]) => deals.reduce((s, d) => s + d.valueEur, 0);

  return (
    <Card padding="sm">
      <ul className="space-y-1.5 text-body-sm text-text-secondary">
        {overdue.length > 0 && (
          <li className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <Badge variant="warning">Overdue close date</Badge>
            <span>
              <span className="font-medium text-text-primary">{plural(overdue.length, 'open deal')}</span> ·{' '}
              {formatMoneyCompact(sum(overdue))} past {overdue.length === 1 ? 'its' : 'their'} close date:{' '}
              {named.map((d, i) => (
                <span key={d.id}>
                  {i > 0 && ', '}
                  <span className="text-text-primary">{d.companyName ?? humanizeDealName(d.name)}</span>{' '}
                  <span className="text-text-tertiary">{formatShortDate(d.expectedCloseDate)}</span>
                </span>
              ))}
              {overdue.length > named.length && <> and {overdue.length - named.length} more</>}
            </span>
          </li>
        )}
        {placeholder.length > 0 && (
          <li className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <Badge variant="warning">Default close date</Badge>
            <span>
              <span className="font-medium text-text-primary">{plural(placeholder.length, 'deal')}</span> ·{' '}
              {formatMoneyCompact(sum(placeholder))} on the default 15 Jan 2027 close date: re-date {placeholder.length === 1 ? 'it' : 'them'} in Attio
            </span>
          </li>
        )}
        {staleCount > 0 && (
          <li className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <Badge variant="warning">Stale</Badge>
            <span>
              <span className="font-medium text-text-primary">{plural(staleCount, 'deal')}</span> ·{' '}
              {formatMoneyCompact(staleValueEur)} with no stage change and no account activity in {data.staleAfterDays}+ days ·{' '}
              <button type="button" className="text-accent underline-offset-2 hover:underline" onClick={onOpenStale}>
                Review the list
              </button>
            </span>
          </li>
        )}
      </ul>
    </Card>
  );
}

/** A thin ink bar on a quiet track, for a 0–1 share of the largest value. */
function ThinBar({ value }: { value: number }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-secondary">
      <div className="h-full rounded-full bg-text-primary" style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%` }} />
    </div>
  );
}

const WON_KINDS: readonly DealKind[] = ['new', 'expansion', 'renewal', 'pilot'];

type TopHorizon = '6' | '12';
const TOP_HORIZONS = [
  { id: '6', label: 'Next 6 months' },
  { id: '12', label: 'Next 12 months' },
] as const;
const TOP_DEALS_SHOWN = 8;

/**
 * Top open deals = what matters now (same rule as the API's isTopDealCandidate):
 * no renewals, not stale, and a real close date within the horizon. Overdue
 * dates stay in; Attio's 15 Jan 2027 placeholder means "never dated" and is out.
 */
function topDealsWithin(open: TopDeal[], staleIds: Set<string>, months: number): TopDeal[] {
  const horizon = new Date();
  horizon.setMonth(horizon.getMonth() + months);
  const limit = horizon.toISOString().slice(0, 10);
  return open
    .filter(
      (d) =>
        d.kind !== 'renewal' &&
        !staleIds.has(d.id) &&
        !!d.expectedCloseDate &&
        !isPlaceholderCloseDate(d.expectedCloseDate) &&
        d.expectedCloseDate.slice(0, 10) <= limit,
    )
    .sort((a, b) => b.valueEur - a.valueEur);
}

function WonDealsModal({
  isOpen,
  onClose,
  deals,
  filter,
  kinds,
  title,
}: {
  isOpen: boolean;
  onClose: () => void;
  deals: TopDeal[];
  filter: SalesPeriodFilter | null;
  kinds: DealKind[];
  title: string;
}) {
  const shown = deals.filter((d) => kinds.includes(d.kind));
  // Reps (team order) get only wins from the Attio go-live; Salesforce-era wins are listed apart.
  const groups = [
    ...SALES_REPS.map((rep) => ({
      key: rep.slug as string,
      label: rep.name,
      rep,
      deals: shown.filter((d) => d.repSlug === rep.slug && !isPreAttioWin(d.actualCloseDate)),
    })),
    {
      key: 'other',
      label: 'Other / unassigned',
      rep: undefined,
      deals: shown.filter((d) => !d.repSlug && !isPreAttioWin(d.actualCloseDate)),
    },
    {
      key: 'salesforce',
      label: 'Won before Attio (8 Jan 2026), not credited to a rep',
      rep: undefined,
      deals: shown.filter((d) => isPreAttioWin(d.actualCloseDate)),
    },
  ].filter((g) => g.deals.length > 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      title={`${title}, ${describePeriod(filter)}`}
      description={`${plural(shown.length, 'deal')} · ${formatMoneyCompact(shown.reduce((s, d) => s + d.valueEur, 0))}${filter ? ' · marked won in period' : ''}`}
    >
      {groups.length === 0 ? (
        <EmptyState title="No closed-won deals" compact />
      ) : (
        <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
          {groups.map((g) => (
            <div key={g.key}>
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <RepInitials rep={g.rep} />
                  <span className="text-body-sm font-medium text-text-primary truncate">{g.label}</span>
                </div>
                <span className="text-body-xs text-text-tertiary shrink-0">
                  {plural(g.deals.length, 'deal')} ·{' '}
                  <span className="text-text-primary font-medium">{formatMoneyCompact(g.deals.reduce((sum, d) => sum + d.valueEur, 0))}</span>
                </span>
              </div>
              <ul className="divide-y divide-border-light">
                {g.deals.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <p className="text-body-sm text-text-primary truncate" title={d.name}>
                        {humanizeDealName(d.name)}
                      </p>
                      <p className="text-body-xs text-text-tertiary truncate">
                        {[d.companyName ? dealCompanyLine(d.name, d.companyName) : 'No company linked', DEAL_KIND_LABEL[d.kind]].filter(Boolean).join(' · ')} ·{' '}
                        {d.actualCloseDate ? `Closed ${formatCloseDate(d.actualCloseDate)}` : 'No close date'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-body-sm text-text-primary font-medium">{formatMoneyCompact(d.valueEur)}</span>
                      <span className="inline-flex w-7 justify-end">
                        <AttioLink href={d.attioUrl} label={humanizeDealName(d.name)} />
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/** Quarters offered in the period dropdown: every quarter with closed-won value, plus the current one. */
export function quarterOptionsOf(data: SalesOverview | undefined): string[] {
  return [
    ...new Set([currentQuarter(), ...(data?.wonByQuarter ?? []).filter((q) => q.quarter !== 'none').map((q) => q.quarter)]),
  ].sort();
}

/**
 * Pipeline → overview: today's open pipeline (Active is the headline — most of
 * Open is stale), the stage bars, one "closed in <period>" line, the pipeline
 * by quarter and the top open deals.
 */
export function PipelineOverview({
  data,
  filter,
  onFilterChange,
  onOpenStale,
}: {
  data: SalesOverview;
  filter: SalesPeriodFilter | null;
  onFilterChange: (next: SalesPeriodFilter | null) => void;
  onOpenStale: () => void;
}) {
  const [wonModal, setWonModal] = useState<{ title: string; kinds: DealKind[] } | null>(null);

  const [horizon, setHorizon] = useState<TopHorizon>('6');
  const { totals, byStage, wonDeals, openByQuarter, wonByQuarter } = data;
  const relevant = useMemo(
    () => topDealsWithin(data.openDeals, new Set(data.staleDeals.map((d) => d.id)), Number(horizon)),
    [data.openDeals, data.staleDeals, horizon],
  );
  const topOpenDeals = relevant.slice(0, TOP_DEALS_SHOWN);
  const maxStageValue = Math.max(1, ...byStage.map((s) => s.valueEur));
  const quarters = splitDefaultClose(openByQuarter);
  // Scaled on the real quarters only: the default-close group is not a quarter and gets no bar.
  const maxQuarterValue = Math.max(1, ...quarters.rows.map((q) => q.valueEur));
  const wonK = totals.wonByKind;
  const wonByQuarterMap = new Map(wonByQuarter.map((q) => [q.quarter, q]));
  const openWon = (title: string, kinds: DealKind[]) => setWonModal({ title, kinds });
  // One rep owning most top deals is named once in the header; only the others get initials.
  const usualOwner = majorityOwner(topOpenDeals, (d) => d.repSlug);
  const usualOwnerRep = usualOwner ? SALES_REPS.find((r) => r.slug === usualOwner) : undefined;

  const closedNotes = [
    wonK.other.count > 0 && `Includes ${plural(wonK.other.count, 'untyped or automation deal')} (${formatMoneyCompact(wonK.other.valueEur)}).`,
    totals.wonBeforeAttioCount > 0 &&
      `${plural(totals.wonBeforeAttioCount, 'deal')} won before Attio (${formatMoneyCompact(totals.wonBeforeAttioValueEur)}) ${totals.wonBeforeAttioCount === 1 ? 'is' : 'are'} not credited to a rep.`,
    'Pilots are not counted as new business.',
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatTile
          label="Active pipeline"
          value={formatMoneyCompact(totals.activeValueEur)}
          sub={`${plural(totals.activeCount, 'deal')} · stage moved or account active in the last ${data.staleAfterDays} days`}
          size="lg"
        />
        <StatTile label="Open pipeline" value={formatMoneyCompact(totals.openValueEur)} sub={plural(totals.openCount, 'deal')} />
        <StatTile
          label="Stale"
          value={formatMoneyCompact(totals.staleValueEur)}
          sub={`${plural(totals.staleCount, 'deal')} · no stage change or activity in ${data.staleAfterDays}+ days`}
        />
      </div>

      <PipelineExceptions data={data} onOpenStale={onOpenStale} />

      {/* One line for everything closed in the chosen period. */}
      <Card padding="sm">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-body-sm text-text-secondary">
          <span className="text-text-tertiary">Closed in</span>
          <PeriodFilter quarterOptions={quarterOptionsOf(data)} value={filter} onChange={onFilterChange} />
          <span>
            {totals.wonCount > 0 ? (
              <button
                type="button"
                className="font-medium text-text-primary underline-offset-2 hover:underline"
                onClick={() => openWon('Closed won', ['new', 'expansion', 'renewal', 'pilot', 'other'])}
              >
                {formatMoneyCompact(totals.wonValueEur)} won
              </button>
            ) : (
              <span className="font-medium text-text-primary">{formatMoneyCompact(totals.wonValueEur)} won</span>
            )}{' '}
            · {plural(totals.wonCount, 'deal')}
          </span>
          {WON_KINDS.filter((k) => wonK[k].count > 0).map((k) => (
            <button
              key={k}
              type="button"
              className="text-text-tertiary underline-offset-2 hover:text-text-primary hover:underline"
              onClick={() => openWon(`${DEAL_KIND_LABEL[k]} won`, [k])}
            >
              {DEAL_KIND_LABEL[k]} {formatMoneyCompact(wonK[k].valueEur)} · {wonK[k].count}
            </button>
          ))}
          <span>{totals.lostCount} lost</span>
          <InfoTip label="About closed deals">
            <ul className="space-y-1">
              {closedNotes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </InfoTip>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card padding="md">
          <CardHeader title="Open pipeline by stage" />
          {byStage.every((s) => s.count === 0) ? (
            <EmptyState title="No open deals" compact />
          ) : (
            <ul className="space-y-2.5">
              {byStage.map((s) => (
                <li key={s.stage}>
                  <div className="mb-1 flex items-center justify-between text-body-xs text-text-secondary">
                    <span>{formatStage(s.stage)}</span>
                    <span>
                      <span className="font-medium text-text-primary">{formatMoneyCompact(s.valueEur)}</span>{' '}
                      <span className="text-text-tertiary">· {s.count}</span>
                    </span>
                  </div>
                  {/* Thin ink bars, the same as Pipeline by quarter beside it. */}
                  <ThinBar value={s.valueEur / maxStageValue} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Always unfiltered so quarters stay comparable. */}
        <Card padding="md">
          <CardHeader
            title="Pipeline by quarter"
            description={`Open by expected close, won by actual close${
              quarters.defaultClose ? ' · deals on Attio’s default 15 Jan 2027 close date listed apart' : ''
            }`}
          />
          {openByQuarter.length === 0 ? (
            <EmptyState title="No open deals" compact />
          ) : (
            <ul className="space-y-2.5">
              {quarters.rows.map((q) => {
                const won = wonByQuarterMap.get(q.quarter);
                return (
                  <li key={q.quarter}>
                    <div className="flex items-center justify-between text-body-xs text-text-secondary mb-1">
                      <span>{q.label}</span>
                      <span>
                        <span className="text-text-primary font-medium">{formatMoneyCompact(q.valueEur)}</span>{' '}
                        <span className="text-text-tertiary">· {q.count} open</span>
                        {won && won.valueEur > 0 && <span className="text-text-tertiary"> · won {formatMoneyCompact(won.valueEur)}</span>}
                      </span>
                    </div>
                    <ThinBar value={q.valueEur / maxQuarterValue} />
                  </li>
                );
              })}
              {quarters.defaultClose && (
                <li
                  className="cursor-help border-t border-dashed border-border-light pt-2.5"
                  title="Attio fills 15 Jan 2027 when nobody sets a close date: these deals have no forecast quarter yet, so they get no bar. Re-date them in Attio."
                >
                  <div className="flex items-center justify-between text-body-xs text-text-tertiary">
                    <span>Not in a quarter yet: default close date</span>
                    <span>
                      {formatMoneyCompact(quarters.defaultClose.valueEur)} · {quarters.defaultClose.count} open
                    </span>
                  </div>
                </li>
              )}
            </ul>
          )}
        </Card>
      </div>

      <Card padding="md">
        <CardHeader
          title="Top open deals"
          description={[
            `${topOpenDeals.length < relevant.length ? `Top ${topOpenDeals.length} of ${relevant.length}` : plural(relevant.length, 'deal')} by value · new business closing in the next ${horizon} months, active, renewals and undated deals left out`,
            usualOwnerRep ? `owner ${usualOwnerRep.name} unless marked` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
          action={<SegmentedControl options={TOP_HORIZONS} value={horizon} onChange={setHorizon} ariaLabel="Close date horizon" />}
        />
        {topOpenDeals.length === 0 ? (
          <EmptyState title={`No active new-business deals closing in the next ${horizon} months`} compact />
        ) : (
          <ul className="divide-y divide-border-light">
            {topOpenDeals.map((d) => {
              const rep = d.repSlug ? SALES_REPS.find((r) => r.slug === d.repSlug) : undefined;
              return (
                <li key={d.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="flex items-center gap-3 min-w-0">
                    {usualOwner && d.repSlug === usualOwner ? (
                      <span className="h-6 w-6 flex-none" aria-hidden />
                    ) : (
                      <RepInitials rep={rep} />
                    )}
                    <div className="min-w-0">
                      <p className="text-body-sm text-text-primary truncate" title={d.name}>
                        {humanizeDealName(d.name)}
                      </p>
                      <CompanySubLine name={d.companyName} dealName={d.name} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <div className="flex flex-col items-end">
                      <span className="text-body-sm text-text-primary font-medium">{formatMoneyCompact(d.valueEur)}</span>
                      <span className="text-body-xs text-text-tertiary">
                        {formatStage(d.stage)} · {DEAL_KIND_LABEL[d.kind]} · closes {formatShortDate(d.expectedCloseDate)}
                      </span>
                    </div>
                    <span className="inline-flex w-7 justify-end">
                      <AttioLink href={d.attioUrl} label={humanizeDealName(d.name)} />
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <WonDealsModal
        isOpen={wonModal !== null}
        onClose={() => setWonModal(null)}
        deals={wonDeals}
        filter={filter}
        kinds={wonModal?.kinds ?? []}
        title={wonModal?.title ?? ''}
      />
    </div>
  );
}
